import { CdkDrag, CdkDragEnd, CdkDragHandle } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  Signal,
  afterRenderEffect,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { BasicDialogService } from '@principle-theorem/ng-shared';
import { PopupNotification } from '@principle-theorem/notifications';
import { Staffer } from '@principle-theorem/principle-core';
import {
  MicrophoneDenialReason,
  SCRIBE_ERROR_STATUSES,
  SCRIBE_IN_PROGRESS_STATUSES,
  ScribeStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  isSameRef,
  snapshotDefined,
} from '@principle-theorem/shared';
import { fromEvent, of } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';
import { AiScribeSessionService } from '../../services/ai-scribe-session.service';
import {
  IRecorderPosition,
  RecorderPosition,
  RecorderPositionStorageService,
} from '../../services/recorder-position-storage.service';
import { AI_SCRIBE_INSERT_TARGET } from '../../tokens/ai-scribe-insert-target';

const SECONDS_PER_MINUTE = 60;
const MIN_BAND_HEIGHT_PERCENT = 4;
const MAX_BAND_HEIGHT_PERCENT = 100;
const NOMINAL_CARD_WIDTH_PX = 288;
const NOMINAL_CARD_HEIGHT_PX = 200;
const WINDOW_RESIZE_DEBOUNCE_MS = 200;
const READY_TARGET_SETTLE_MS = 250;

export const SCRIBE_NOTE_READY_MESSAGE =
  'Scribe Note ready — review from the record button on the chart';

interface IMicrophoneMessage {
  title: string;
  detail: string;
}

/**
 * Floating, draggable UI for a live AI Scribe session. It never inserts or
 * opens a note — review belongs to the chart's scribe launcher. It only posts a
 * return-link notification when the note is ready and the clinician has left
 * the recorded patient's record, where the launcher can no longer fire.
 */
@Component({
  selector: 'pr-ai-scribe-recorder',
  templateUrl: './ai-scribe-recorder.component.html',
  styleUrls: ['./ai-scribe-recorder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkDrag,
    CdkDragHandle,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
})
export class AiScribeRecorderComponent {
  private readonly _service = inject(AiScribeSessionService);
  private readonly _dialog = inject(BasicDialogService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _org = inject(OrganisationService);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _zone = inject(NgZone);
  private readonly _positionStorage = inject(RecorderPositionStorageService);
  private readonly _insertTarget =
    inject(AI_SCRIBE_INSERT_TARGET, { optional: true }) ?? undefined;
  private readonly _dismissed = signal(false);
  private readonly _minimised = signal(false);
  private _handledReadySessionId?: string;
  private _readySettleArmed = false;
  private readonly _readySettleReached = signal(false);

  private readonly _activeTargetEmission = toSignal(
    (this._insertTarget?.activeTarget$ ?? of(undefined)).pipe(
      map((target) => ({ emitted: true, target }))
    ),
    { initialValue: { emitted: false, target: undefined } }
  );

  private readonly _cardContainer =
    viewChild<ElementRef<HTMLElement>>('cardContainer');

  private _firstVisibleClampApplied = false;

  /** Re-applied after each pill/card `@if` swap so the recorder keeps its place. */
  readonly dragPosition = signal<IRecorderPosition>(this._initialPosition());

  readonly ScribeStatus = ScribeStatus;

  readonly status = this._service.status;
  readonly persistenceError = this._service.persistenceError;
  readonly boundPatient = this._service.boundPatient;
  readonly microphoneDenialReason = this._service.microphoneDenialReason;
  readonly canFinishInBackground = this._service.canFinishInBackground;

  readonly patientName = computed(() => this.boundPatient()?.name ?? '');

  readonly elapsedLabel = computed(() =>
    this._formatMinutesSeconds(this._service.elapsed())
  );

  readonly writingElapsedLabel = computed(() =>
    this._formatMinutesSeconds(this._service.writingElapsed())
  );

  readonly writingTitle = computed(() =>
    this._service.stillWriting()
      ? 'Still writing your note…'
      : 'Finishing notes…'
  );

  readonly writingDetail = computed(() =>
    this._service.stillWriting()
      ? `You can keep working. The note will be added to this patient's scribe notes when it's ready.`
      : 'This can take a few minutes.'
  );

  readonly failureTitle = computed(() =>
    this._service.noteRecoveryPending()
      ? 'Note still being written.'
      : 'AI Scribe session failed.'
  );

  readonly failureDetail = computed(
    () => this._service.failureMessage() ?? 'Please try again.'
  );

  readonly statusLabel = computed(() => {
    switch (this.status()) {
      case ScribeStatus.RequestingPermission:
      case ScribeStatus.Connecting:
        return 'Connecting';
      case ScribeStatus.PermissionDenied:
        return 'Blocked';
      case ScribeStatus.Writing:
        return 'Saving';
      case ScribeStatus.Failed:
        return 'Failed';
      default:
        return 'Rec';
    }
  });

  readonly microphoneMessage: Signal<IMicrophoneMessage> = computed(() =>
    this.microphoneDenialReason() === MicrophoneDenialReason.Unavailable
      ? {
          title: 'No microphone found.',
          detail: 'Connect a microphone, then try again.',
        }
      : {
          title: 'Microphone access denied.',
          detail:
            'Check browser settings to allow the microphone, then try again.',
        }
  );

  readonly bars = computed(() =>
    Array.from(this._service.bandLevels()).map((level) =>
      Math.min(
        MAX_BAND_HEIGHT_PERCENT,
        Math.max(MIN_BAND_HEIGHT_PERCENT, level * MAX_BAND_HEIGHT_PERCENT)
      )
    )
  );

  readonly visible = computed(() => {
    const status = this.status();
    if (SCRIBE_IN_PROGRESS_STATUSES.includes(status)) {
      return true;
    }
    if (SCRIBE_ERROR_STATUSES.includes(status)) {
      return !this._dismissed();
    }
    return false;
  });

  readonly isErrorState = computed(() =>
    SCRIBE_ERROR_STATUSES.includes(this.status())
  );

  readonly showPill = computed(() => this._minimised() && !this.isErrorState());

  readonly currentUrl = toSignal(
    this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this._router.url)
    ),
    { initialValue: this._router.url }
  );

  readonly showReturn = computed(() => {
    const launch = this._service.launchUrl();
    return (
      launch !== undefined && launch !== this.currentUrl() && this.visible()
    );
  });

  constructor() {
    effect(() => {
      const status = this.status();
      if (
        status === ScribeStatus.RequestingPermission ||
        status === ScribeStatus.Connecting ||
        status === ScribeStatus.InProgress
      ) {
        this._dismissed.set(false);
        this._minimised.set(false);
      }
    });

    effect(() => {
      const session = this._service.activeSession();
      if (this.status() !== ScribeStatus.Ready || session === undefined) {
        return;
      }
      if (this._handledReadySessionId === session.ref.id) {
        return;
      }
      // Wait for the target's first emission (or a short settle) so its initial
      // `undefined` doesn't trigger a spurious off-context popup.
      const emission = this._activeTargetEmission();
      if (!emission.emitted && !this._readySettleReached()) {
        this._armReadySettle();
        return;
      }
      this._handledReadySessionId = session.ref.id;
      const target = emission.target;
      const patient = this.boundPatient();
      const patientMatches =
        target !== undefined &&
        patient !== undefined &&
        isSameRef(target.patientRef, patient.ref);
      if (patientMatches) {
        return;
      }
      void this._notifyReadyOffContext();
    });

    // The construction-time clamp used nominal dimensions; re-clamp once the
    // card is in the DOM and measurable.
    afterRenderEffect(() => {
      if (
        this._cardContainer() === undefined ||
        this._firstVisibleClampApplied
      ) {
        return;
      }
      this._firstVisibleClampApplied = true;
      this._reclampToViewport();
    });

    // Outside NgZone so each raw resize event doesn't trigger app-wide change
    // detection; the signal write in `_reclampToViewport` schedules its own render.
    this._zone.runOutsideAngular(() =>
      fromEvent<Event>(window, 'resize')
        .pipe(
          debounceTime(WINDOW_RESIZE_DEBOUNCE_MS),
          takeUntilDestroyed(this._destroyRef)
        )
        .subscribe(() => this._reclampToViewport())
    );
  }

  async stop(): Promise<void> {
    await this._service.stopSession();
  }

  async discard(): Promise<void> {
    const confirmed = await this._dialog.confirm({
      title: 'Discard AI Scribe session',
      prompt: 'Discard this recording? Any notes captured will be lost.',
    });
    if (confirmed === true) {
      await this._service.discardSession();
    }
  }

  async retry(): Promise<void> {
    await this._service.retrySession();
  }

  finishInBackground(): void {
    void this._service.finishInBackground();
  }

  dismiss(): void {
    this._dismissed.set(true);
  }

  toggleMinimise(): void {
    this._minimised.update((minimised) => !minimised);
  }

  navigateBack(): void {
    const launch = this._service.launchUrl();
    if (launch === undefined) {
      return;
    }
    void this._router.navigateByUrl(launch);
  }

  /** Clamps before persisting so a drag released off-screen snaps back into view. */
  onDragEnded(event: CdkDragEnd): void {
    const dragged = event.source.getFreeDragPosition();
    const clamped = RecorderPosition.clampToViewport(
      dragged,
      this._measureCardRect(),
      {
        width: window.innerWidth,
        height: window.innerHeight,
      }
    );
    this.dragPosition.set(clamped);
    this._positionStorage.save(clamped);
  }

  /**
   * Posts a popup linking back to the launch route, falling back to a snackbar.
   * Writes to `popupNotificationCol` directly because importing
   * `PopupNotificationsService` from `ng-interactions` would create a dependency
   * cycle. Only the primary-outlet path, query params and fragment are kept,
   * which covers the clinical-notes routes.
   */
  private async _notifyReadyOffContext(): Promise<void> {
    const launch = this._service.launchUrl();
    if (launch === undefined) {
      this._snackBar.open(SCRIBE_NOTE_READY_MESSAGE);
      return;
    }
    try {
      const tree = this._router.parseUrl(launch);
      const [path = launch] = launch.split(/[?#]/);
      const queryParams =
        Object.keys(tree.queryParams).length > 0 ? tree.queryParams : undefined;
      const fragment = tree.fragment ?? undefined;
      const staffer = await snapshotDefined(this._org.staffer$);
      await Firestore.addDoc(
        Staffer.popupNotificationCol(staffer),
        PopupNotification.init({
          title: 'Scribe Note ready',
          content: `Recording for ${
            this.patientName() || 'your patient'
          } has finished. Return to where you started to review and insert the note.`,
          hasTimeout: false,
          actions: [
            {
              label: 'Review Scribe Note',
              href: path,
              ...(queryParams && { queryParams }),
              ...(fragment && { fragment }),
              colour: 'primary',
            },
          ],
        })
      );
    } catch {
      this._snackBar.open(SCRIBE_NOTE_READY_MESSAGE);
    }
  }

  private _armReadySettle(): void {
    if (this._readySettleArmed) {
      return;
    }
    this._readySettleArmed = true;
    const settleTimer = setTimeout(
      () => this._readySettleReached.set(true),
      READY_TARGET_SETTLE_MS
    );
    this._destroyRef.onDestroy(() => clearTimeout(settleTimer));
  }

  private _initialPosition(): IRecorderPosition {
    const stored = this._positionStorage.load() ?? { x: 0, y: 0 };
    return RecorderPosition.clampToViewport(
      stored,
      { width: NOMINAL_CARD_WIDTH_PX, height: NOMINAL_CARD_HEIGHT_PX },
      { width: window.innerWidth, height: window.innerHeight }
    );
  }

  private _measureCardRect(): { width: number; height: number } {
    const card = this._cardContainer();
    if (card === undefined) {
      return { width: NOMINAL_CARD_WIDTH_PX, height: NOMINAL_CARD_HEIGHT_PX };
    }
    const rect = card.nativeElement.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return { width: NOMINAL_CARD_WIDTH_PX, height: NOMINAL_CARD_HEIGHT_PX };
    }
    return { width: rect.width, height: rect.height };
  }

  private _reclampToViewport(): void {
    const current = this.dragPosition();
    const clamped = RecorderPosition.clampToViewport(
      current,
      this._measureCardRect(),
      {
        width: window.innerWidth,
        height: window.innerHeight,
      }
    );
    if (clamped.x === current.x && clamped.y === current.y) {
      return;
    }
    this.dragPosition.set(clamped);
    this._positionStorage.save(clamped);
  }

  private _formatMinutesSeconds(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
    const seconds = totalSeconds % SECONDS_PER_MINUTE;
    return `${this._padTwoDigits(minutes)}:${this._padTwoDigits(seconds)}`;
  }

  private _padTwoDigits(value: number): string {
    return value.toString().padStart(2, '0');
  }
}
